import { CartAddEvent } from '@theme/events';

/**
 * Cart "Complete Your Grow" recommendations (see snippets/cart-summary-extras.liquid).
 *
 * The cart drawer is refreshed with morph.js, which rebuilds the DOM from HTML parsed
 * by DOMParser. Parsed <script> tags never execute, so handlers defined inline in the
 * snippet are undefined after the drawer morphs in (the common empty-cart-on-load case),
 * and clicking "Add to Cabinet" did nothing. This module loads once from the header and
 * delegates off `document`, so it keeps working across every morph.
 */

const ADD_SELECTOR = '.cronk-mini-item__add';
const SIZE_SELECTOR = '.cronk-mini-item__select';
const CARD_SELECTOR = '.cronk-mini-item';

// Update the displayed price when a size is chosen.
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches(SIZE_SELECTOR)) return;

  const card = select.closest(CARD_SELECTOR);
  const price = select.options[select.selectedIndex]?.dataset.price;
  const priceEl = card?.querySelector('.cronk-mini-item__price');
  if (priceEl && price) priceEl.textContent = price;

  const addButton = card?.querySelector(ADD_SELECTOR);
  if (addButton instanceof HTMLButtonElement) {
    addButton.dataset.variantId = select.value;
  }
});

// Add the recommended product when "Add to Cabinet" is clicked.
document.addEventListener('click', (event) => {
  const target = event.target;
  const button = target instanceof Element ? target.closest(ADD_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  addRecommendation(button);
});

/**
 * @param {HTMLButtonElement} button
 */
async function addRecommendation(button) {
  const card = button.closest(CARD_SELECTOR);
  if (!card) return;

  // Read the variant from this card's own select (multi-size) or the button's data
  // attribute (single variant). Avoids getElementById, which grabs the wrong element
  // when the same recommendation renders in both the drawer and the cart page.
  const select = card.querySelector(SIZE_SELECTOR);
  const rawId = select instanceof HTMLSelectElement ? select.value : button.dataset.variantId;
  const variantId = parseInt(rawId ?? '', 10);
  if (!variantId) return;

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding...';

  const sectionIds = getCartSectionIds();

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
    const data = await response.json();

    button.textContent = 'Added!';
    button.classList.add('cronk-mini-item__add--done');

    if (data.sections) {
      // Native refresh: morphs the drawer (and cart page) in place, keeps the drawer open.
      // Pass the new cart total so cart-icon.js keeps the header bubble (and its sessionStorage
      // cache) in sync. /cart/add.js returns only the added line, not the cart total, so read it
      // from the returned section HTML — the same hidden element component-cart-items.js uses.
      document.dispatchEvent(
        new CartAddEvent({}, 'cart-recs', {
          source: 'cart-recs',
          didError: false,
          productId: card.getAttribute('data-product-id') ?? undefined,
          variantId: String(variantId),
          itemCount: getNewItemCount(data.sections),
          sections: data.sections,
        })
      );
    } else {
      // Fallback if the section render is unavailable for any reason.
      window.location.reload();
    }
  } catch (error) {
    console.error('[cart-recs]', error);
    button.disabled = false;
    button.textContent = originalText;
  }
}

/**
 * Collects the section ids that render cart contents so the add request returns fresh
 * HTML for each (mirrors component-cart-items.js).
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = el.getAttribute('data-section-id');
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

/**
 * Reads the new total cart item count from the returned section HTML (the hidden
 * [ref="cartItemCount"] element rendered by cart-products.liquid).
 * @param {Record<string, string>} sections
 * @returns {number | undefined}
 */
function getNewItemCount(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string') continue;
    const raw = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
    if (raw != null) {
      const count = parseInt(raw, 10);
      if (!Number.isNaN(count)) return count;
    }
  }
  return undefined;
}

// --- Estimated delivery date (also previously in the snippet's inline script) ---

function updateDeliveryEstimate() {
  const dateEl = document.querySelector('[data-standard-delivery]');
  if (!dateEl) return;

  const fulfillmentDays = 1;
  const transitDays = 5;
  const cutoffHour = 14;

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) added++;
    }
    return result;
  };

  const now = new Date();
  const fulfillStart = new Date(now);
  if (now.getHours() >= cutoffHour) fulfillStart.setDate(fulfillStart.getDate() + 1);
  if (fulfillStart.getDay() === 0) fulfillStart.setDate(fulfillStart.getDate() + 1);

  const shipDate = addBusinessDays(fulfillStart, fulfillmentDays);
  const deliveryDate = addBusinessDays(shipDate, transitDays);

  dateEl.textContent = deliveryDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

updateDeliveryEstimate();
// The element is replaced when the drawer morphs after a cart update; recompute after morph.
document.addEventListener('cart:update', () => requestAnimationFrame(updateDeliveryEstimate));
